"use client";

import { SearchIcon, XIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../cn";
import { Button } from "./button";
import { CountPill } from "./count-pill";
import { FIELD_FOCUS, FOCUS_RING } from "./focus-ring";
import { HOVER_EASE } from "./states";

// Design spec §6.2 toolbar: a 40 px row on a --border-subtle rule. Left: scope / count / search;
// right (`ml-auto` on the first right-hand item): Filter, Sort, Group, Columns as tinted ghost
// buttons, then the primary action.

export type ToolbarProps = React.ComponentProps<"div"> & {
  /** Full-bleed bodies (tables, boards) pad 24 px; inside a page frame the row has no padding. */
  bleed?: boolean;
};

export function Toolbar({ bleed = false, className, ...props }: ToolbarProps) {
  return (
    <div
      data-slot="toolbar"
      className={cn(
        // Narrow screens scroll the row sideways rather than wrap it; the 2 px inner gutter keeps
        // the end buttons' focus outlines inside the scroll box.
        "flex h-10 min-w-0 items-center gap-2 strip-scroll border-b border-subtle",
        bleed ? "px-6 max-md:px-4" : "-mx-0.5 px-0.5",
        className,
      )}
      {...props}
    />
  );
}

type IconType = React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>;

export type ToolbarButtonProps = Omit<React.ComponentProps<typeof Button>, "children"> & {
  icon: IconType;
  label: string;
  /** Tinted when a filter, sort or grouping is in effect. */
  active?: boolean;
  /** Active filter count, shown as an accent pill. */
  count?: number;
  /** Below this width the label is visually hidden (the button keeps its name). */
  collapseLabelBelow?: 1440 | false;
};

export function ToolbarButton({
  icon: Icon,
  label,
  active = false,
  count,
  collapseLabelBelow = 1440,
  className,
  ...props
}: ToolbarButtonProps) {
  return (
    <Button
      data-slot="toolbar-button"
      data-active={active || undefined}
      variant="ghost"
      size="sm"
      className={cn(
        "data-active:bg-primary-soft data-active:text-primary-text data-active:hover:bg-primary-soft data-active:hover:text-primary-text",
        collapseLabelBelow === 1440 && "max-[1440px]:px-1.5",
        className,
      )}
      {...props}
    >
      <Icon aria-hidden={true} />
      <span className={collapseLabelBelow === 1440 ? "max-[1440px]:sr-only" : undefined}>
        {label}
      </span>
      {count ? <CountPill count={count} tone="accent" /> : null}
    </Button>
  );
}

export type SearchFieldProps = Omit<React.ComponentProps<"input">, "type"> & {
  "aria-label"?: string;
};

/** 220 × 28 search box with the icon 8 px in; "Search in view…" by default. */
export function SearchField({
  className,
  placeholder = "Search in view…",
  "aria-label": ariaLabel = "Search in view",
  ...props
}: SearchFieldProps) {
  return (
    <div data-slot="search-field" className={cn("relative w-[220px] max-sm:w-full", className)}>
      <SearchIcon
        aria-hidden={true}
        className="pointer-events-none absolute top-1/2 left-2 size-3.5 -translate-y-1/2 text-faint"
      />
      <input
        type="search"
        aria-label={ariaLabel}
        placeholder={placeholder}
        className={cn(
          "h-7 w-full min-w-0 rounded-md border border-input bg-card pr-2 pl-7 text-sm text-foreground shadow-sm placeholder:text-muted-foreground hover:border-strong max-md:text-md",
          HOVER_EASE,
          FIELD_FOCUS,
        )}
        {...props}
      />
    </div>
  );
}

/** The active-filter row under the toolbar: 36 px, chips 6 px apart. */
export function FilterChips({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="filter-chips"
      className={cn("flex min-h-9 flex-wrap items-center gap-1.5", className)}
      {...props}
    />
  );
}

export type FilterChipProps = Omit<React.ComponentProps<"span">, "children"> & {
  label: React.ReactNode;
  onRemove?: () => void;
  /** Accessible name of the remove button. */
  removeLabel?: string;
};

/** 12 px chip on --secondary, radius 6, with a 16 px remove button. */
export function FilterChip({
  label,
  onRemove,
  removeLabel = "Remove filter",
  className,
  ...props
}: FilterChipProps) {
  return (
    <span
      data-slot="filter-chip"
      className={cn(
        "inline-flex h-6 items-center gap-1 rounded-sm bg-secondary pr-1 pl-2 text-xs text-secondary",
        !onRemove && "pr-2",
        className,
      )}
      {...props}
    >
      {label}
      {onRemove ? (
        <button
          type="button"
          aria-label={removeLabel}
          onClick={onRemove}
          className={cn(
            "inline-flex size-4 items-center justify-center rounded-xs text-muted-foreground hover:bg-active hover:text-foreground",
            HOVER_EASE,
            FOCUS_RING,
          )}
        >
          <XIcon className="size-3" aria-hidden={true} />
        </button>
      ) : null}
    </span>
  );
}
